// Shared state machine for smart-page capture / integrate / live-preview
// embed blocks.
//
// One `useEmbedRecords` instance owns an array of records that the user
// is editing inside a single embed block. Each record carries a
// lifecycle status (`retrieved` → `dirty` after edit, `unsaved` →
// `saved` after a successful batch). The hook handles:
//
//   • Retrieving existing rows from the server (filtered by the embed's
//     resolved filters)
//   • Adding new local records, optionally seeded from filter values,
//     authored arg mappings, and server-side resolve-defaults
//   • Patching a record's header / detail rows (auto-flips retrieved →
//     dirty)
//   • Batch-saving every changed record through livePreviewProcess and
//     merging the per-record outcome back into local state
//
// The Angular smart-page runtime mirrors this state machine in its own
// service so capture/integrate behaves identically in designer canvas
// and runtime preview.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import type { BatchScope, ListFilter } from '../screen/contract'
import type { EmbedDataApi } from './contract'
import { attemptWithWarningConfirm } from '../warnings'
import type { TxnNotifier } from '../context'
import type { DataAccess } from '../../dataBinding'
import type {
  EmbedDetailMap,
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice, RecordStatus,
  WarningItem,
} from '../contracts'
import type { SqlModel, TransactionModel } from '../types'
import {
  columnFormKey, literalDefaults, parseApiError,
} from '../utils'
import { applySeededItemChangeCascades, applyRowEvent } from '../list/useItemChange'
import { annotateRowError, markChildrenSaved } from '../screen/detailsBuffer'
import { mergePeople, type PeopleDirectory } from '../people'

// ── Types ────────────────────────────────────────────────────────────

/**
 * In-flight record managed by `useEmbedRecords`. The client-side ``id``
 * (a generated UUID) is the stable identifier for every record — the
 * server's primary key may be unassigned (status==='unsaved') or may
 * change shape between retrieve / save, but `id` stays the same so
 * react keys, selection state, and validation maps stay coherent.
 */
export interface EmbedRecord {
  /** Stable client-side id. Used as React key + selection target. */
  id: string
  /** Server-assigned primary key, if any. Set after retrieve or save. */
  primaryKey?: string
  /** Lifecycle status. */
  status: RecordStatus
  /** Header column → value. */
  header: Record<string, unknown>
  /**
   * Snapshot of the header as it was at the last retrieve / successful
   * save — i.e. the canonical server-side baseline. Used to compute
   * column-level deltas for smart-doc persistence and to apply only
   * the user's *actual* edits on top of a refreshed server fetch, so
   * concurrent edits by other users on other columns aren't lost.
   * Unset for `unsaved` records (no server counterpart yet).
   */
  originalHeader?: Record<string, unknown>
  /** Per-detail-table rows. Keyed by main_table name. */
  details: EmbedDetailMap
  /** Field → error message map, populated from the last batch save. */
  fieldErrors: Record<string, string>
  /** Record-level error message (i.e. ``_record`` from the backend). */
  recordError: string | null
  /** Banner message — last save's first non-field error or summary. */
  lastSavedAt?: string
  /**
   * Engine-reported editability of the persisted record (``_editable`` on the
   * list row): false ⇒ the designer's edit-lock rule matches it, or it is
   * awaiting approval. The host opens such a record view-only — fields
   * disabled, action bar and Save gone — and the engine refuses the write
   * anyway. Undefined for records the server has not spoken about yet
   * (unsaved), which are editable by definition.
   */
  editable?: boolean
  /** Why the record is locked (``_lock_reason``), shown to the user. */
  lockReason?: string
  /**
   * Row-scoped actions the engine hid for THIS record (``_hidden_actions`` on
   * the list row) — their ``visible_when`` resolved false against its values.
   * The row's menu drops them. Undefined for a record the server has not
   * spoken about yet (unsaved), which offers the full menu.
   */
  hiddenActions?: string[]
  /**
   * The header values last seeded from the embed's `recordVar` (a JSON page
   * variable). Hook-internal (not sent to the server). Lets the reactive
   * re-seed update only fields the user hasn't manually changed since the
   * last seed, so an on-upload extraction can refresh the form without
   * clobbering edits.
   */
  _recordVarApplied?: Record<string, unknown>
  /**
   * Per-column protect overrides ({form_key: 1|0}) returned by the seeded
   * item_change / row_add cascades that ran while this record was created.
   * Hook-internal — the form surface seeds its dynamic-protect state from
   * this so a conditionally-disabled column opens disabled (the cascades
   * only fire again on a live change).
   */
  _cascadeProtect?: Record<string, 0 | 1>
}

/** Resolved (already-substituted) filter set the embed renders with. */
export type ResolvedFilters = ListFilter[]

/** Page-input values map used for filter resolution + variable seeding. */
export type PageInputValues = Record<string, unknown>

/**
 * Authored filter clause from `embed.filters`. Same shape as the embed
 * designer stores; values are still `{source, name|value}` refs and
 * are resolved against `inputValues` inside the hook.
 */
export interface AuthoredFilter {
  column?: string
  op?: ListFilter['op']
  value?: { source: 'static' | 'page_input'; name?: string; value?: string }
  from?: { source: 'static' | 'page_input'; name?: string; value?: string }
  to?: { source: 'static' | 'page_input'; name?: string; value?: string }
}

/** Authored arg mapping from `embed.args`. */
export interface AuthoredArg {
  target: string
  source?: { source: 'static' | 'page_input'; name?: string; value?: string }
}

export interface UseEmbedRecordsOpts {
  /** Backend data adapter (list / resolve-defaults / execute / batch save).
   *  Injected — the package carries no HTTP client of its own. The platform
   *  passes its embedDataApi adapter; the standalone runtime its own. */
  api: EmbedDataApi
  /** Host notifier for informational save prompts (severity-'P'). Optional —
   *  prompts are dropped silently when absent. */
  notify?: TxnNotifier
  /** Project the transaction lives in. Required for any server roundtrip. */
  project: string
  /** Transaction obj_name. Required. */
  objName: string
  /** Live page-input map (read each call). Used by filter resolution +
   *  variable seeding on Add. */
  inputValues: PageInputValues
  /** Authored embed.filters (resolved per-call against `inputValues`). */
  filters: AuthoredFilter[]
  /** Authored embed.args. Seeded into the header on Add. */
  args?: AuthoredArg[]
  /** Name of a JSON page variable (`embed.recordVar`) whose object value seeds
   *  the (add) form by matching its keys to columns. Re-seeds reactively when
   *  the variable changes (e.g. after an on-upload extraction), without
   *  clobbering fields the user already edited. */
  recordVar?: string
  /** Main sql_model for the transaction. When supplied, ``addNew``
   *  seeds literal ``default_value`` from metadata into the new
   *  record's header (mirroring LivePreviewTab.startAdd). Token-valued
   *  defaults (audit, project var, auto_generate) are excluded — those
   *  are resolved server-side. */
  sm?: SqlModel | null
  /** Full transaction model. Required for nested (3rd-level+) detail forms:
   *  the batch save resolves each detail form's own child forms from it to
   *  commit grandchildren in dependency order. Optional — when absent the
   *  save behaves exactly as the 2-level path. */
  model?: TransactionModel | null
  /** When set, the hook calls `livePreviewResolveDefaults` after the
   *  client-side seed so server-stamped project variables fill in too.
   *  Defaults to true. */
  useResolveDefaults?: boolean
  /** Page maximum rows to retrieve. Defaults to 50. */
  pageSize?: number
  /** Routes all server round-trips (list / defaults / save / item_change) to
   *  the site-txn path in site mode, so the embed works for end-users on a
   *  published page. Defaults to undefined → author-only platform endpoints. */
  dataAccess?: DataAccess
  /** End-user-adjusted values for the active filter set's conditions, keyed by
   *  column db_name. Shared with the List view so switching to multi-tabular
   *  keeps the same enforced filter rather than loading the full table. */
  enforcedOverrides?: Record<string, { from?: string; to?: string; value?: string }>
  /** Which designer-defined filter set is active (falls back to default). */
  filterSetId?: string
  /** When true, bypass the enforced filter and retrieve all records. */
  bypassEnforced?: boolean
  /** The list tab the screen is on (`list_view.tabs`). */
  listTab?: string | null
  /** "Ignore warnings and proceed" confirm. When a record's save is blocked
   *  ONLY by ignorable business-logic warnings (422 VALIDATION_WARNINGS), the
   *  hook calls this with the warnings; resolving true retries that record
   *  with the acknowledged codes in `ignore_warnings`. Absent ⇒ warnings
   *  block exactly like errors (fail-closed). Host components supply
   *  `useWarningConfirm().confirmWarnings` and render its modal. */
  confirmWarnings?: (warnings: WarningItem[]) => Promise<boolean>
}

export interface UseEmbedRecordsResult {
  records: EmbedRecord[]
  /** Records grouped by status, for the toolbar summary chip. */
  counts: Record<RecordStatus, number>
  busy: boolean
  /** Server-side total for the most recent retrieve. */
  total: number

  /** Active set's resolved enforced conditions, echoed from the last
   *  retrieve. Drives the enforced-filter value bar in the tabular view so
   *  switching from the list keeps the filter visible + adjustable. */
  enforcedFilters?: EnforcedConditionEcho[]
  /** Designer-defined filter sets the end user can switch between. */
  filterSets?: EnforcedFilterSetEcho[]
  /** Non-blocking messages from the conditions' own validations. */
  filterNotices?: FilterNotice[]
  /** Which set produced ``enforcedFilters`` (null when bypassed). */
  activeFilterSetId: string | null
  /** True when the last retrieve bypassed the enforced filter. */
  bypassed: boolean
  /**
   * Who the retrieved rows' user-key columns point at, as the engine named
   * them — null until a retrieve lands, or when the object has no such
   * column. Fed to <PeopleProvider> so every cell reads the person rather
   * than the key (see `txn/people`).
   */
  people: PeopleDirectory | null
  /** Last batch-save outcome (top-level success + any record-level
   *  ``status: failed/partial`` rows). */
  lastSaveError: string | null

  /**
   * Pull rows from the server matching the embed's resolved filters
   * and merge them into ``records`` with status='retrieved'. Already-
   * present rows (matched by primary_key) are refreshed in place so the
   * user's `unsaved`/`dirty` work isn't blown away by a refetch.
   */
  retrieve: () => Promise<void>

  /**
   * Append a new record with status='unsaved'. The header is pre-
   * filled by, in order of precedence: explicit ``seed`` argument,
   * authored `embed.args`, `embed.filters` with op==='eq' & a resolved
   * page_input value, and finally `livePreviewResolveDefaults` (when
   * enabled). Later sources never override earlier ones.
   */
  addNew: (seed?: Record<string, unknown>) => Promise<EmbedRecord>

  /** Replace a record's header values. Auto-flips retrieved → dirty. */
  updateHeader: (id: string, patch: Record<string, unknown>) => void

  /** Replace a record's detail rows for one table. Auto-flips
   *  retrieved → dirty. */
  updateDetails: (
    id: string, table: string, rows: Array<Record<string, unknown>>,
  ) => void

  /** Functional detail-row update for the inline child grids — merges
   *  batched (Excel paste) edits, flips retrieved/saved → dirty, and records
   *  an undo checkpoint (coalesced per cell via `coalesceKey`). */
  updateDetailRows: (
    id: string, table: string,
    updater: (prev: Array<Record<string, unknown>>) => Array<Record<string, unknown>>,
    coalesceKey?: string,
  ) => void

  /** Seed a record's detail rows from a server fetch without dirtying it or
   *  touching undo history — used to lazy-load existing children on expand. */
  seedDetails: (
    id: string, table: string, rows: Array<Record<string, unknown>>,
  ) => void

  /** Seed a (3rd-level) grandchild's rows under a specific saved child row,
   *  without dirtying — used to lazy-load existing grandchildren when a child
   *  row is expanded. Matches the child row by its server _txn_id. */
  seedRowChildren: (
    id: string, childFormNo: string, childTxnId: string,
    gcFormNo: string, rows: Array<Record<string, unknown>>,
  ) => void

  /** Remove a record from local state. Server-side delete is not
   *  attempted here — capture/integrate works offline; deletes should
   *  be a separate explicit action when needed. */
  removeLocal: (id: string) => void

  /**
   * Drop every record that has unflushed local work (status in
   * `unsaved`, `dirty`, `failed`, `partial`). `retrieved` and `saved`
   * rows survive — they're the canonical-from-server set. Used by the
   * Retrieve dialog when the user picks "Discard local edits and
   * retrieve" so a subsequent retrieve() pulls a clean view without
   * the merge logic preserving the stale local copies.
   */
  discardLocalEdits: () => void

  /**
   * Put every unsaved record back exactly as it was SEEDED through
   * `EmbedDocStateContext.initialRecords` — the discard for a grid that never
   * retrieves (`autoRetrieve={false}`, e.g. the Assistant's file import),
   * where `discardLocalEdits` would drop the seeded rows and nothing would
   * bring them back. Records already `saved` stay as saved; rows added since
   * are dropped; seeded rows removed since come back. A no-op returning false
   * when the hook was not seeded.
   */
  restoreSeed: () => boolean

  /**
   * The records as SEEDED, by client id — the baseline a review screen
   * compares against to tell a change the seed brought in (seed vs
   * `originalHeader`) from one the user made since (current vs seed).
   * Null when the hook was not seeded. Never mutated.
   */
  seeded: ReadonlyMap<string, EmbedRecord> | null

  /** True when at least one record carries unflushed local work. Used
   *  by the UI to prompt before retrieve / on filter changes. */
  hasPendingChanges: boolean

  /** Persist every record with status in (`unsaved`,`dirty`) via
   *  livePreviewProcess. Returns the top-level outcome. */
  saveBatch: () => Promise<{ success: boolean; failedCount: number }>

  /** Hard-reset local state (used when obj_name changes). */
  reset: () => void

  /**
   * Step backward / forward through the local *edit* history — cell
   * edits, paste, fill, add-row, remove-row. Typing into one cell
   * coalesces into a single step; a paste / fill is one step. Server
   * operations (retrieve / saveBatch / discard / reset) clear the
   * history so undo never crosses a canonical-state boundary.
   */
  undo: () => void
  redo: () => void
  /** Whether an undo / redo step is available (for toolbar enablement). */
  canUndo: boolean
  canRedo: boolean
}

// ── Document-mode persistence bridge ─────────────────────────────────
//
// When the embed runs inside a smart document, the host wraps it in
// the context below so any unsynced rows survive a doc reopen.
// `initialRecords` seeds the hook on mount; `onRecordsChange` fires
// whenever the in-memory records change so the host can persist them
// into the embed block's per-document properties. Outside a document
// (page designer canvas, internal/external page preview) the context
// is `null` and the hook falls back to its empty default.
//
// Persisted records are confined to those carrying *unflushed* local
// work — status in `unsaved`, `dirty`, `failed`, `partial`. Anything
// with `retrieved` or `saved` is canonical server data and gets
// re-fetched by the next retrieve() merge, so persisting it would
// double the payload for no gain.

export interface EmbedDocState {
  /** Records to seed the hook with on mount. */
  initialRecords?: EmbedRecord[]
  /**
   * Fires after every records mutation with the subset that should
   * persist (unsynced rows only). Suppressed for the initial seed.
   */
  onRecordsChange?: (records: EmbedRecord[]) => void
}

export const EmbedDocStateContext = createContext<EmbedDocState | null>(null)

/** Rows that need to survive a doc reopen — anything the server
 *  doesn't already hold a canonical copy of. */
function isUnsyncedRecord(r: EmbedRecord): boolean {
  return r.status === 'unsaved'
      || r.status === 'dirty'
      || r.status === 'failed'
      || r.status === 'partial'
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Deep copy of seeded records (plain JSON-shaped data from the host). */
function cloneRecords(recs: EmbedRecord[]): EmbedRecord[] {
  try {
    if (typeof structuredClone === 'function') return structuredClone(recs)
  } catch (err) {
    console.warn('[useEmbedRecords] structuredClone failed; falling back to JSON copy', err)
  }
  return JSON.parse(JSON.stringify(recs)) as EmbedRecord[]
}

function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

function resolveRef(
  ref: { source: 'static' | 'page_input'; name?: string; value?: string } | undefined,
  inputValues: PageInputValues,
): unknown {
  if (!ref) return undefined
  if (ref.source === 'page_input') {
    if (!ref.name) return undefined
    const v = inputValues[ref.name]
    if (v === undefined || v === null || v === '') return undefined
    return v
  }
  const s = ref.value
  if (s === undefined || s === null || s === '') return undefined
  return s
}

// Mirror backend column_db_name: lowercase + collapse whitespace to "_".
// Without this normalisation a filter authored as e.g. "Customer Name"
// or "FirstName" never matches the backend's main_col_set (which is
// keyed on db_name), so the filter is silently dropped and the list
// returns unfiltered rows.
function normalizeFilterColumn(col: string): string {
  return (col || '').trim().replace(/\s+/g, '_').toLowerCase()
}

function resolveFilters(
  filters: AuthoredFilter[],
  inputValues: PageInputValues,
): ResolvedFilters {
  return filters
    .filter((f): f is AuthoredFilter & { column: string; op: ListFilter['op'] } =>
      !!f.column && !!f.op)
    .map((f) => {
      const column = normalizeFilterColumn(f.column)
      if (f.op === 'between') {
        return {
          column,
          op: f.op,
          from: resolveRef(f.from, inputValues),
          to: resolveRef(f.to, inputValues),
        }
      }
      return {
        column,
        op: f.op,
        value: resolveRef(f.value, inputValues),
      }
    })
}

// Map a raw target / filter-column key (the designer stores these as
// c.name, the column's display name) to the column's normalized db_name
// so the seed lands under the same key FormRenderer reads,
// applySeededItemChangeCascades scans, and livePreviewProcess expects.
// Falls back to whitespace-collapsed-lowercased raw key when the
// metadata is missing or the key matches no column.
function resolveSeedKey(rawKey: string, sm: SqlModel | null): string {
  const k = (rawKey || '').trim()
  if (!k) return ''
  if (sm) {
    const lk = k.toLowerCase()
    for (const w of sm.columns || []) {
      const c = w?.column
      if (!c) continue
      if ((c.name || '').toLowerCase() === lk
          || (c.db_name || '').toLowerCase() === lk) {
        return columnFormKey(c)
      }
    }
  }
  return k.replace(/\s+/g, '_').toLowerCase()
}

/**
 * Build initial header values for a new record from filters + args.
 * Precedence (highest first): caller seed, authored args, eq-filter
 * with resolved page_input, server defaults. The caller layers
 * server defaults on top later via `livePreviewResolveDefaults`.
 */
function seedFromFiltersAndArgs(
  filters: AuthoredFilter[],
  args: AuthoredArg[],
  inputValues: PageInputValues,
  sm: SqlModel | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}

  // 1. Authored args[] — explicit column targets the embed author
  //    declared, e.g. capture's "preload column X with selected_id".
  for (const a of args) {
    const target = resolveSeedKey(a.target || '', sm)
    if (!target) continue
    const v = resolveRef(a.source, inputValues)
    if (v === undefined) continue
    out[target] = v
  }

  // 2. eq-filter columns bound to a resolved page_input. Mirrors
  //    EmbedLivePreview.startAdd — adding a Task to a selected Project
  //    pre-fills `project_id` from `selected_project_id`.
  for (const f of filters) {
    if (f.op !== 'eq') continue
    const col = resolveSeedKey(f.column || '', sm)
    if (!col || col in out) continue
    const v = resolveRef(f.value, inputValues)
    if (v === undefined) continue
    out[col] = v
  }

  return out
}

/**
 * Seed header values from a JSON object held in a `recordVar` page variable,
 * matching each object key to a transaction column (by name / db_name). Keys
 * that match no column are ignored when the model is known, so an extraction
 * payload with extra fields doesn't pollute the form. Values map to the same
 * form keys FormRenderer reads.
 */
function seedFromRecordVar(
  obj: unknown, sm: SqlModel | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return out
  const knownKeys = sm
    ? new Set((sm.columns || [])
        .map((w) => w?.column)
        .filter(Boolean)
        .flatMap((c) => [(c!.name || '').toLowerCase(), (c!.db_name || '').toLowerCase()])
        .filter(Boolean))
    : null
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v === undefined) continue
    if (knownKeys && !knownKeys.has(k.toLowerCase())) continue
    const target = resolveSeedKey(k, sm)
    if (!target) continue
    out[target] = v
  }
  return out
}

/**
 * The DETAIL rows a `recordVar` draft carries, mapped onto each child form.
 *
 * `__details__` is keyed by child form_no. Header seeding alone was not
 * enough for the thing the prefill exists for: a page cart becomes an ORDER,
 * and an order is its lines. Without this an embed opened from a draft
 * showed the customer and an empty grid, and the basket had to be keyed in
 * again by hand.
 *
 * Each row's keys go through the same `resolveSeedKey` the header uses, so a
 * draft may name a column either way (PRODUCT_CODE or product_code), and a
 * key matching no column of that form is dropped rather than carried into
 * the save.
 */
export const RECORD_VAR_DETAILS_KEY = '__details__'

function detailsFromRecordVar(
  obj: unknown, model: TransactionModel | null | undefined,
): EmbedDetailMap {
  const out: EmbedDetailMap = {}
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return out
  const raw = (obj as Record<string, unknown>)[RECORD_VAR_DETAILS_KEY]
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out
  for (const [formNo, rows] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(rows)) continue
    const childSm = sqlModelByFormNo(model, formNo)
    const mapped: Array<Record<string, unknown>> = []
    for (const row of rows) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) continue
      const seeded = seedFromRecordVar(row, childSm)
      if (Object.keys(seeded).length) mapped.push(seeded)
    }
    // An EMPTY list is still an answer — it clears the grid. Only a form
    // that the draft never mentioned is left alone.
    out[String(formNo)] = mapped
  }
  return out
}

/** The sql_model whose form_no matches, by declared value or 1-based position. */
function sqlModelByFormNo(
  model: TransactionModel | null | undefined, formNo: string,
): SqlModel | null {
  const entries = model?.sql_models || []
  const target = String(formNo || '').trim()
  if (!target) return null
  for (let i = 0; i < entries.length; i++) {
    const sm = entries[i]?.sql_model
    if (!sm) continue
    const own = String(sm.form_no || '').trim() || String(i + 1)
    if (own === target) return sm
  }
  return null
}

function recordFromOutcomeRow(
  row: Record<string, unknown> & { _txn_id: string },
): EmbedRecord {
  const pk = row._txn_id
  const headerCopy = { ...row }
  delete (headerCopy as { _txn_id?: string })._txn_id
  // Engine annotations, not columns — lift them off the header so they never
  // ride back to the server on a save.
  const editable = headerCopy._editable
  const lockReason = headerCopy._lock_reason
  const hiddenActions = headerCopy._hidden_actions
  delete (headerCopy as { _editable?: unknown })._editable
  delete (headerCopy as { _lock_reason?: unknown })._lock_reason
  delete (headerCopy as { _hidden_actions?: unknown })._hidden_actions
  return {
    id: uuid(),
    primaryKey: pk,
    status: 'retrieved',
    header: headerCopy,
    editable: editable === undefined ? undefined : editable !== false,
    lockReason: lockReason === undefined ? undefined : String(lockReason),
    hiddenActions: Array.isArray(hiddenActions)
      ? hiddenActions.filter((n): n is string => typeof n === 'string')
      : undefined,
    // Capture the server-side baseline so a future smart-doc reopen can
    // re-apply only the columns the user actually changed on top of a
    // refreshed fetch (preserving concurrent edits by other users).
    originalHeader: { ...headerCopy },
    details: {},
    fieldErrors: {},
    recordError: null,
  }
}

function statusCounts(records: EmbedRecord[]): Record<RecordStatus, number> {
  const z: Record<RecordStatus, number> = {
    retrieved: 0, dirty: 0, unsaved: 0,
    saved: 0, failed: 0, partial: 0,
  }
  for (const r of records) z[r.status] = (z[r.status] || 0) + 1
  return z
}

// Spread the server's ``validationErrors[]`` into a (fieldErrors,
// recordError) pair. The field name `_record` (or any unknown column)
// becomes the record-level banner.
// markChildrenSaved / annotateRowError moved to the shared package
// (txn/screen detailsBuffer) so the shared transaction screen and these
// embeds run the SAME buffer semantics; re-exported here so existing
// importers keep working.
export { annotateRowError, markChildrenSaved }

// ── Hook ─────────────────────────────────────────────────────────────

export function useEmbedRecords(opts: UseEmbedRecordsOpts): UseEmbedRecordsResult {
  const {
    api, notify,
    project, objName, inputValues, filters, args = [], sm = null,
    model = null, useResolveDefaults = true, pageSize = 50, dataAccess,
    enforcedOverrides, filterSetId, bypassEnforced, listTab, recordVar,
    confirmWarnings,
  } = opts
  // Injected transport/notifier behind refs so callback identities stay
  // exactly as they were when these were module-level imports.
  const apiRef = useRef(api)
  apiRef.current = api
  const notifyRef = useRef(notify)
  notifyRef.current = notify
  const daRef = useRef(dataAccess)
  daRef.current = dataAccess
  const confirmWarningsRef = useRef(confirmWarnings)
  confirmWarningsRef.current = confirmWarnings
  const modelRef = useRef(model)
  modelRef.current = model
  // Enforced-filter selection, read at retrieve time so a parent change does
  // not have to restart the retrieve callback.
  const enfOverridesRef = useRef(enforcedOverrides)
  enfOverridesRef.current = enforcedOverrides
  const filterSetIdRef = useRef(filterSetId)
  filterSetIdRef.current = filterSetId
  const bypassRef = useRef(bypassEnforced)
  bypassRef.current = bypassEnforced
  const listTabRef = useRef(listTab)
  listTabRef.current = listTab

  // Smart-doc persistence bridge. Null outside a document instance
  // (page designer / internal-page preview), in which case the hook
  // behaves like the standalone version. Inside a document, the host
  // supplies the saved unsynced records and a writer that fans every
  // mutation back into the block's per-document properties.
  const docState = useContext(EmbedDocStateContext)

  const [records, setRecords] = useState<EmbedRecord[]>(
    () => docState?.initialRecords ?? [],
  )
  // A private copy of the seed, taken once, for restoreSeed(). Deep-cloned so
  // no later mutation of a live record's nested rows can reach it.
  const seedRef = useRef<EmbedRecord[] | null>(null)
  if (seedRef.current === null && docState?.initialRecords) {
    seedRef.current = cloneRecords(docState.initialRecords)
  }
  const seeded = useMemo<ReadonlyMap<string, EmbedRecord> | null>(
    () => (seedRef.current ? new Map(seedRef.current.map((r) => [r.id, r])) : null),
    [],
  )
  const [busy, setBusy] = useState(false)
  const [total, setTotal] = useState(0)
  const [lastSaveError, setLastSaveError] = useState<string | null>(null)
  // Enforced-filter echo from the most recent retrieve — surfaced so the
  // tabular view can render the same set picker + value bar the list shows.
  const [enforcedFilters, setEnforcedFilters] = useState<EnforcedConditionEcho[] | undefined>()
  const [filterSets, setFilterSets] = useState<EnforcedFilterSetEcho[] | undefined>()
  const [filterNotices, setFilterNotices] = useState<FilterNotice[] | undefined>()
  const [activeFilterSetId, setActiveFilterSetId] = useState<string | null>(null)
  const [bypassed, setBypassed] = useState(false)
  // Names for the user keys the retrieved rows carry (see `txn/people`).
  const [people, setPeople] = useState<PeopleDirectory | null>(null)

  // ── Undo / redo history ───────────────────────────────────────────
  //
  // Snapshots of the immutable `records` array captured *before* each
  // user edit. Because every mutation produces a brand-new array (and
  // new record objects), an old snapshot is a safe, frozen view of the
  // past — restoring it is just `setRecords(snapshot)`.
  //
  // `recordsRef` mirrors the latest committed records so a checkpoint can
  // read "the state right now" without the callback depending on
  // `records` (which would re-create every edit handler each keystroke).
  // During a synchronous paste/fill loop the ref intentionally stays
  // stale, so all N writes share one pre-edit checkpoint = one undo step.
  const recordsRef = useRef(records)
  useEffect(() => { recordsRef.current = records }, [records])

  const historyRef = useRef<{
    past: EmbedRecord[][]
    future: EmbedRecord[][]
    lastPrev: EmbedRecord[] | null
    lastKey: string | null
  }>({ past: [], future: [], lastPrev: null, lastKey: null })
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  const HISTORY_LIMIT = 100

  const syncHistoryFlags = useCallback(() => {
    setCanUndo(historyRef.current.past.length > 0)
    setCanRedo(historyRef.current.future.length > 0)
  }, [])

  // Record a pre-edit checkpoint. `coalesceKey` collapses consecutive
  // edits that share it (e.g. successive keystrokes in the same cell)
  // into one undo step. The `lastPrev === prev` guard makes a synchronous
  // batch (paste/fill loop) — and React StrictMode's double-invoke — push
  // at most one checkpoint.
  const checkpoint = useCallback((coalesceKey?: string) => {
    const h = historyRef.current
    const prev = recordsRef.current
    if (h.lastPrev === prev) {
      if (coalesceKey) h.lastKey = coalesceKey
      return
    }
    if (coalesceKey && coalesceKey === h.lastKey) {
      h.lastPrev = prev
      return
    }
    h.past.push(prev)
    if (h.past.length > HISTORY_LIMIT) h.past.shift()
    h.future = []
    h.lastPrev = prev
    h.lastKey = coalesceKey ?? null
    syncHistoryFlags()
  }, [syncHistoryFlags])

  const clearHistory = useCallback(() => {
    historyRef.current = { past: [], future: [], lastPrev: null, lastKey: null }
    syncHistoryFlags()
  }, [syncHistoryFlags])

  const undo = useCallback(() => {
    const h = historyRef.current
    if (h.past.length === 0) return
    const prevState = h.past.pop()!
    h.future.push(recordsRef.current)
    h.lastPrev = null
    h.lastKey = null
    recordsRef.current = prevState
    setRecords(prevState)
    syncHistoryFlags()
  }, [syncHistoryFlags])

  const redo = useCallback(() => {
    const h = historyRef.current
    if (h.future.length === 0) return
    const nextState = h.future.pop()!
    h.past.push(recordsRef.current)
    h.lastPrev = null
    h.lastKey = null
    recordsRef.current = nextState
    setRecords(nextState)
    syncHistoryFlags()
  }, [syncHistoryFlags])

  // Push every records mutation back into the doc-state bridge so the
  // host can persist unsynced work into __doc_state__. Skip the very
  // first run — it's the seeded value, already up to date with the
  // host's saved snapshot, so re-emitting would round-trip a no-op.
  const onRecordsChange = docState?.onRecordsChange
  const skipNextChangeRef = useRef(true)
  useEffect(() => {
    if (!onRecordsChange) return
    if (skipNextChangeRef.current) {
      skipNextChangeRef.current = false
      return
    }
    onRecordsChange(records.filter(isUnsyncedRecord))
  }, [records, onRecordsChange])

  // Read-only-by-design refs so callbacks don't restart on every value
  // change. The Angular runtime will use the equivalent inject(...) +
  // signal reads — same semantics.
  const inputValuesRef = useRef(inputValues)
  inputValuesRef.current = inputValues
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const argsRef = useRef(args)
  argsRef.current = args
  const smRef = useRef(sm)
  smRef.current = sm
  const recordVarRef = useRef(recordVar)
  recordVarRef.current = recordVar

  // Reset on obj_name change so a re-pointed embed doesn't carry stale
  // records from the previous transaction.
  const objKey = `${project}::${objName}`
  const lastObjKeyRef = useRef(objKey)
  useEffect(() => {
    if (lastObjKeyRef.current !== objKey) {
      lastObjKeyRef.current = objKey
      setRecords([])
      setTotal(0)
      setLastSaveError(null)
    }
  }, [objKey])

  const counts = useMemo(() => statusCounts(records), [records])

  const reset = useCallback(() => {
    setRecords([])
    setTotal(0)
    setLastSaveError(null)
    clearHistory()
  }, [clearHistory])

  const retrieve = useCallback(async () => {
    if (!project || !objName) return
    setBusy(true)
    try {
      const resolved = resolveFilters(filtersRef.current, inputValuesRef.current)
      const res = await apiRef.current.list(
        daRef.current, project, objName, pageSize, 0, undefined, undefined, resolved,
        enfOverridesRef.current,
        { filterSetId: filterSetIdRef.current, bypass: bypassRef.current,
          ...(listTabRef.current ? { tab: listTabRef.current } : {}) },
      )
      setTotal(res.total || 0)
      // Surface the enforced-filter echo so the tabular view's filter
      // controls reflect what the server actually applied.
      setEnforcedFilters(res.enforced_filters)
      setFilterSets(res.enforced_filter_sets)
      setFilterNotices(res.filter_notices)
      setActiveFilterSetId(res.active_filter_set_id ?? null)
      setBypassed(!!res.bypassed)
      // Who the rows' user-key columns point at, as the engine named them —
      // so an embedded grid reads "kumar.pawar", not `443a07c7-526c-…`.
      setPeople((prev) => mergePeople(prev, res))
      const fresh = (res.rows || []).map(
        (row) => recordFromOutcomeRow(row as Record<string, unknown> & { _txn_id: string }),
      )
      // Merge with preserved unsynced rows so a smart-doc reopen that
      // seeds the hook from saved state lands on a coherent view:
      //
      //   - For `dirty` rows: compute the columns the user actually
      //     changed (header[k] !== originalHeader[k]) and re-apply
      //     them on top of the fresh server row. Concurrent edits by
      //     other users on *other* columns survive because we only
      //     override the columns the local user touched.
      //   - For `unsaved` rows (no primary_key, new record): append.
      //   - For `failed` / `partial` rows: replace the fresh server
      //     copy with the local one so the validation banners survive
      //     and the user can fix + retry without losing context.
      //
      // Records that lost their server counterpart (deleted between
      // sessions) are kept too so the user notices their work isn't
      // dangling unrendered. saveBatch will then create a new row.
      setRecords((prev) => {
        const preserved = prev.filter(isUnsyncedRecord)
        if (preserved.length === 0) return fresh

        const preservedByPk = new Map<string, EmbedRecord>()
        const preservedNoPk: EmbedRecord[] = []
        for (const p of preserved) {
          if (p.primaryKey) preservedByPk.set(p.primaryKey, p)
          else preservedNoPk.push(p)
        }

        const merged: EmbedRecord[] = []
        for (const fr of fresh) {
          const local = fr.primaryKey ? preservedByPk.get(fr.primaryKey) : undefined
          if (!local) { merged.push(fr); continue }
          preservedByPk.delete(fr.primaryKey!)
          if (local.status === 'dirty') {
            // Compute and re-apply per-column deltas on top of fr.
            const baseline = local.originalHeader ?? {}
            const deltas: Record<string, unknown> = {}
            for (const k of Object.keys(local.header)) {
              if (local.header[k] !== baseline[k]) deltas[k] = local.header[k]
            }
            merged.push({
              ...fr,
              id: local.id,
              status: 'dirty',
              header: { ...fr.header, ...deltas },
              // Fresh server values become the new baseline so a
              // subsequent edit computes deltas against current truth.
              originalHeader: { ...fr.header },
              details: local.details,
            })
            continue
          }
          // failed / partial — keep the local copy intact so the
          // user can address the validation errors before retrying.
          merged.push(local)
        }
        // Preserved rows that no longer match any server PK (deleted
        // upstream) — keep them so the user's work isn't silently
        // discarded; they'll re-create on the next save.
        for (const stray of preservedByPk.values()) merged.push(stray)
        // New rows the user added locally (no PK yet) — append.
        merged.push(...preservedNoPk)
        return merged
      })
      setLastSaveError(null)
      // The records were just replaced with a fresh server-merged view —
      // undoing across it would resurrect stale local snapshots.
      clearHistory()
    } finally {
      setBusy(false)
    }
  }, [project, objName, pageSize, clearHistory])

  const addNew = useCallback(async (
    seed?: Record<string, unknown>,
  ): Promise<EmbedRecord> => {
    const literal = literalDefaults(smRef.current)
    const localSeed = seedFromFiltersAndArgs(
      filtersRef.current, argsRef.current, inputValuesRef.current,
      smRef.current,
    )
    // recordVar: a JSON page variable spread onto matching columns (e.g. data
    // extracted from an uploaded document). Above args/filters; below an
    // explicit caller seed.
    const recordSeed = recordVarRef.current
      ? seedFromRecordVar(inputValuesRef.current[recordVarRef.current], smRef.current)
      : {}
    // Precedence (highest first): explicit caller seed > recordVar > authored
    // args / filter seed > literal metadata defaults > server-resolved
    // project variables (layered in below).
    let header: Record<string, unknown> = {
      ...literal, ...localSeed, ...recordSeed, ...(seed || {}),
    }

    if (useResolveDefaults && project && objName) {
      try {
        const r = await apiRef.current.resolveDefaults(daRef.current, project, objName)
        if (r?.values && typeof r.values === 'object') {
          // Server defaults are the LOWEST priority — keep literal
          // defaults / authored args / filter seeds / explicit caller
          // seed on top.
          header = { ...r.values, ...header }
        }
      } catch {
        // Non-fatal — local seeds still apply.
      }
    }

    // Cascade item_change for every column that ended up seeded with a
    // non-empty value. Without this step, a column populated from a
    // page input or arg seed would show its raw code but the dependent
    // description column (filled by item_change) would stay blank.
    // Protect overrides the cascades return are collected so the form
    // surface can open with conditionally-disabled columns already locked.
    const cascadeProtect: Record<string, 0 | 1> = {}
    const collectProtect = (res: { protect?: Record<string, 0 | 1> }) => {
      if (res.protect) Object.assign(cascadeProtect, res.protect)
    }
    header = await applySeededItemChangeCascades(
      apiRef.current.execute, daRef.current, project, objName, smRef.current, header,
      undefined, undefined, collectProtect,
    )

    // Form-level row_add event: run the form's whole-row definition (rule /
    // code) and merge its values in — the form-level cousin of item_change.
    header = await applyRowEvent(
      apiRef.current.execute, daRef.current, project, objName, smRef.current, 'row_add', header,
      undefined, undefined, collectProtect,
    )

    // Detail rows the draft carries (a page cart's lines, typically). They
    // go through the SAME item_change cascade the header just did, per row:
    // the cart sends a product code and a quantity, and the line's own
    // item_change is what fills the description, the unit price and the tax
    // rate FROM THE MASTER. Skipping that would leave the grid showing a
    // bare code and a blank price until the operator touched each row.
    const seededDetails = recordVarRef.current
      ? detailsFromRecordVar(inputValuesRef.current[recordVarRef.current], modelRef.current)
      : {}
    for (const [formNo, rows] of Object.entries(seededDetails)) {
      const childSm = sqlModelByFormNo(modelRef.current, formNo)
      if (!childSm || !Array.isArray(rows) || rows.length === 0) continue
      const cascaded: Array<Record<string, unknown>> = []
      for (const row of rows) {
        try {
          cascaded.push(await applySeededItemChangeCascades(
            apiRef.current.execute, daRef.current, project, objName, childSm, row,
            header, String(formNo),
          ))
        } catch {
          // A line the master cannot price still belongs in the grid — the
          // save will refuse it with a field error the operator can see,
          // which beats dropping it silently.
          cascaded.push(row)
        }
      }
      seededDetails[formNo] = cascaded
    }

    const rec: EmbedRecord = {
      id: uuid(),
      status: 'unsaved',
      header,
      details: seededDetails,
      fieldErrors: {},
      recordError: null,
      _recordVarApplied: recordVarRef.current ? recordSeed : undefined,
      _cascadeProtect: Object.keys(cascadeProtect).length ? cascadeProtect : undefined,
    }
    checkpoint()
    setRecords((prev) => [...prev, rec])
    return rec
  }, [project, objName, useResolveDefaults, checkpoint])

  // Reactive re-seed: when the bound `recordVar` JSON variable changes (e.g.
  // an on-upload extraction populates it after the form is already showing),
  // refresh the matching fields of any unsaved record that was recordVar-seeded
  // — but only fields the user hasn't manually changed since the last seed.
  const recordVarSig = recordVar
    ? JSON.stringify((inputValues as Record<string, unknown>)[recordVar] ?? null)
    : ''
  useEffect(() => {
    if (!recordVar) return
    const newSeed = seedFromRecordVar(
      (inputValuesRef.current as Record<string, unknown>)[recordVar], smRef.current,
    )
    if (Object.keys(newSeed).length === 0) return
    setRecords((prev) => {
      let changed = false
      const next = prev.map((r) => {
        if (r.status !== 'unsaved' || r._recordVarApplied === undefined) return r
        const applied = r._recordVarApplied || {}
        const patch: Record<string, unknown> = {}
        const nextApplied: Record<string, unknown> = { ...applied }
        for (const [k, v] of Object.entries(newSeed)) {
          const untouched = !(k in applied) || r.header[k] === applied[k]
          if (!untouched) continue
          if (r.header[k] !== v) patch[k] = v
          nextApplied[k] = v
        }
        if (Object.keys(patch).length === 0) return r
        changed = true
        return { ...r, header: { ...r.header, ...patch }, _recordVarApplied: nextApplied }
      })
      return changed ? next : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordVar, recordVarSig])

  const updateHeader = useCallback((id: string, patch: Record<string, unknown>) => {
    // Coalesce consecutive edits to the same cell(s) into one undo step.
    checkpoint(`h:${id}:${Object.keys(patch).sort().join(',')}`)
    setRecords((prev) => prev.map((r) => {
      if (r.id !== id) return r
      const nextStatus: RecordStatus =
        r.status === 'retrieved' ? 'dirty' :
        r.status === 'saved' ? 'dirty' :
        r.status
      // A cell the user (or a cascade) has just rewritten no longer carries
      // the message about its OLD value — the next save re-validates it. Left
      // in place, a fixed cell would stay red and keep counting as an error.
      let fieldErrors = r.fieldErrors
      if (fieldErrors && Object.keys(patch).some((k) => k in fieldErrors)) {
        fieldErrors = { ...fieldErrors }
        for (const k of Object.keys(patch)) delete fieldErrors[k]
      }
      return {
        ...r,
        header: { ...r.header, ...patch },
        status: nextStatus,
        fieldErrors,
      }
    }))
  }, [checkpoint])

  const updateDetails = useCallback((
    id: string, table: string, rows: Array<Record<string, unknown>>,
  ) => {
    checkpoint(`d:${id}:${table}`)
    setRecords((prev) => prev.map((r) => {
      if (r.id !== id) return r
      const nextStatus: RecordStatus =
        r.status === 'retrieved' ? 'dirty' :
        r.status === 'saved' ? 'dirty' :
        r.status
      return {
        ...r,
        details: { ...r.details, [table]: rows },
        status: nextStatus,
      }
    }))
  }, [checkpoint])

  // Functional detail-row update — used by the inline child grids so a
  // synchronous Excel paste loop (many writes in one tick) merges cleanly
  // (each call sees the latest array). Flips retrieved/saved → dirty and
  // records an undo checkpoint (coalesced per cell via `coalesceKey`).
  const updateDetailRows = useCallback((
    id: string, table: string,
    updater: (prev: Array<Record<string, unknown>>) => Array<Record<string, unknown>>,
    coalesceKey?: string,
  ) => {
    checkpoint(coalesceKey ? `dr:${id}:${table}:${coalesceKey}` : `dr:${id}:${table}`)
    setRecords((prev) => prev.map((r) => {
      if (r.id !== id) return r
      const cur = (r.details[table] as Array<Record<string, unknown>>) || []
      const next = updater(cur)
      if (next === cur) return r
      const nextStatus: RecordStatus =
        r.status === 'retrieved' ? 'dirty' :
        r.status === 'saved' ? 'dirty' :
        r.status
      return { ...r, details: { ...r.details, [table]: next }, status: nextStatus }
    }))
  }, [checkpoint])

  // Seed a record's detail rows from a server fetch WITHOUT marking it dirty
  // or touching undo history — used to lazy-load existing children on expand.
  const seedDetails = useCallback((
    id: string, table: string, rows: Array<Record<string, unknown>>,
  ) => {
    setRecords((prev) => prev.map((r) => {
      if (r.id !== id) return r
      // Only seed when the table hasn't been populated yet — so a slow
      // lazy-load that resolves after the user already started editing (or a
      // double fetch) never clobbers in-progress child edits.
      if (r.details[table] !== undefined) return r
      return { ...r, details: { ...r.details, [table]: rows } }
    }))
  }, [])

  // Seed a grandchild form's rows under a specific (already-saved) child row,
  // without dirtying the record — the nested analogue of seedDetails. Matches
  // the child row by its server _txn_id and only seeds when not already
  // present, so in-progress nested edits are never clobbered.
  const seedRowChildren = useCallback((
    id: string, childFormNo: string, childTxnId: string,
    gcFormNo: string, rows: Array<Record<string, unknown>>,
  ) => {
    setRecords((prev) => prev.map((r) => {
      if (r.id !== id) return r
      const childRows = (r.details[childFormNo] as Array<Record<string, unknown>>) || []
      let changed = false
      const nextChildRows = childRows.map((cr) => {
        if (String(cr._txn_id ?? '') !== childTxnId) return cr
        const existing = (cr.__children as EmbedDetailMap | undefined)
        if (existing && existing[gcFormNo] !== undefined) return cr
        changed = true
        return { ...cr, __children: { ...(existing || {}), [gcFormNo]: rows } }
      })
      if (!changed) return r
      return { ...r, details: { ...r.details, [childFormNo]: nextChildRows } }
    }))
  }, [])

  const removeLocal = useCallback((id: string) => {
    checkpoint()
    setRecords((prev) => prev.filter((r) => r.id !== id))
  }, [checkpoint])

  const discardLocalEdits = useCallback(() => {
    setRecords((prev) => prev.filter(
      (r) => r.status === 'retrieved' || r.status === 'saved',
    ))
    setLastSaveError(null)
    clearHistory()
  }, [clearHistory])

  const restoreSeed = useCallback((): boolean => {
    const seed = seedRef.current
    if (!seed) return false
    const fresh = cloneRecords(seed)
    setRecords((prev) => {
      const saved = new Map(
        prev.filter((r) => r.status === 'saved').map((r) => [r.id, r]),
      )
      return fresh.map((r) => saved.get(r.id) ?? r)
    })
    setLastSaveError(null)
    clearHistory()
    return true
  }, [clearHistory])

  const hasPendingChanges = useMemo(
    () => records.some(
      (r) => r.status === 'unsaved' || r.status === 'dirty'
          || r.status === 'failed' || r.status === 'partial',
    ),
    [records],
  )

  const saveBatch = useCallback(async (): Promise<{ success: boolean; failedCount: number }> => {
    if (!project || !objName) return { success: false, failedCount: 0 }
    setLastSaveError(null)

    const sendable = records.filter(
      (r) => r.status === 'unsaved' || r.status === 'dirty' || r.status === 'failed' || r.status === 'partial',
    )
    if (sendable.length === 0) {
      return { success: true, failedCount: 0 }
    }

    setBusy(true)
    let failedCount = 0
    try {
      // Each record is one transaction instance: its header plus the WHOLE
      // nested child/grandchild tree commit atomically in a single request, or
      // roll back together (no orphaned header on a child failure). Records are
      // independent business transactions, saved one at a time so a failure in
      // one never touches another. The hook's `details` map (keyed by child
      // form_no, rows carrying _txn_id/_edited/_deleted/__children) is exactly
      // the batch payload shape — sent verbatim; the engine threads the parent
      // PK down and skips re-writing untouched rows.
      for (const rec of sendable) {
        try {
          // When the host supplies a confirmWarnings handler, a record whose
          // save is blocked only by ignorable warnings opens the confirm and
          // (on proceed) retries with the acknowledged codes. Without a
          // handler the warnings block exactly like errors (fail-closed).
          const attempt = (ignore: string[]) =>
            apiRef.current.executeBatch(daRef.current, project, objName, {
              header: rec.header,
              details: rec.details,
              ...(rec.primaryKey ? { txn_id: rec.primaryKey } : {}),
              ...(ignore.length ? { ignore_warnings: ignore } : {}),
            })
          const confirm = confirmWarningsRef.current
          const result = confirm
            ? await attemptWithWarningConfirm(attempt, confirm)
            : await attempt([])
          // Informational severity-'P' prompts — the record saved fine.
          result.prompts?.forEach((p) => {
            const n = notifyRef.current
            if (n) (n.info ?? n.success)(p.message)
          })
          // Refresh the server-side baseline so later edits delta against the
          // just-saved row, and fold the returned child tree (now carrying each
          // row's server _txn_id) back into local state, matched by _localId.
          const nextHeader = { ...rec.header, ...(result.header || {}) }
          const savedDetails = markChildrenSaved(result.details || {})
          setRecords((prev) => prev.map((r) => (r.id === rec.id ? {
            ...r,
            status: 'saved' as RecordStatus,
            primaryKey: result.txn_id || r.primaryKey,
            header: nextHeader,
            originalHeader: { ...nextHeader },
            details: savedDetails,
            fieldErrors: {},
            recordError: null,
            lastSavedAt: new Date().toISOString(),
          } : r)))
        } catch (err) {
          failedCount++
          const parsed = parseApiError(err)
          const scope = (parsed.details as { scope?: BatchScope } | null)?.scope ?? null
          const fieldErrors: Record<string, string> = {}
          let recordError: string | null = null
          for (const ve of parsed.validationErrors || []) {
            if (ve.field && ve.field !== '_record') fieldErrors[ve.field] = ve.message
            else if (!recordError) recordError = ve.message
          }
          recordError = recordError || parsed.message || 'Save failed'
          // Atomic save ⇒ nothing persisted ⇒ the whole record is 'failed'
          // (never the old 'partial'). Route field errors to the header (empty
          // scope path) or to the exact failing child row (non-empty path) so
          // the grid highlights the right cell.
          const path = scope?.path
          const headerLevel = !path || path.length === 0
          setRecords((prev) => prev.map((r) => {
            if (r.id !== rec.id) return r
            if (headerLevel) {
              return { ...r, status: 'failed' as RecordStatus, fieldErrors, recordError }
            }
            return {
              ...r,
              status: 'failed' as RecordStatus,
              fieldErrors: {},
              recordError,
              details: annotateRowError(r.details, path, fieldErrors, recordError || ''),
            }
          }))
        }
      }

      const ok = failedCount === 0
      if (!ok) {
        setLastSaveError(
          `${failedCount} record${failedCount === 1 ? '' : 's'} failed to save`,
        )
      }
      // Saved rows now carry server-side identity/baseline — undoing past a
      // save would desync local state from what was persisted.
      clearHistory()
      return { success: ok, failedCount }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed'
      setLastSaveError(msg)
      return { success: false, failedCount: sendable.length }
    } finally {
      setBusy(false)
    }
  }, [project, objName, records, clearHistory])

  return {
    records, counts, busy, total, lastSaveError, hasPendingChanges,
    enforcedFilters, filterSets, filterNotices, activeFilterSetId, bypassed, people,
    retrieve, addNew, updateHeader, updateDetails, updateDetailRows, seedDetails,
    seedRowChildren,
    removeLocal, discardLocalEdits, restoreSeed, seeded, saveBatch, reset,
    undo, redo, canUndo, canRedo,
  }
}
